import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { useT, type Lang } from "@/lib/i18n";
import { useState } from "react";

type Option = {
  code: Lang;
  label: string;
  native: string;
  region: string;
  flag: string;
};

const OPTIONS: Option[] = [
  { code: "es", label: "Spanish", native: "Español", region: "España", flag: "🇪🇸" },
  { code: "pt", label: "Portuguese", native: "Português", region: "Portugal / Brasil", flag: "🇵🇹" },
  { code: "en", label: "English", native: "English", region: "United Kingdom / USA", flag: "🇬🇧" },
];

export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { lang, setLang, tk, availableLocales } = useT();
  const [open, setOpen] = useState(false);
  const options = OPTIONS.filter((o) => availableLocales.includes(o.code));
  const current = options.find((o) => o.code === lang) ?? options[0] ?? OPTIONS[0];

  if (options.length <= 1) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={`group inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 bg-primary-foreground/10 transition-colors hover:border-primary-foreground/70 ${
          compact ? "px-2.5 py-1.5" : "px-3.5 py-2"
        }`}
        aria-label={`${current.native} — ${tk("langswitch.74604f", "Cambiar idioma")}`}
      >
        <span className="text-base leading-none">{current.flag}</span>

        {!compact && (
          <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-primary-foreground/80 group-hover:text-primary-foreground transition-colors">
            {current.code}
          </span>
        )}
        <ChevronDown size={12} className={`text-primary-foreground/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-64 p-0 overflow-hidden rounded-2xl border border-gold/25 bg-background/95 backdrop-blur-xl shadow-2xl shadow-black/40"
      >
        <div className="px-5 pt-4 pb-3 border-b border-gold/15">
          <p className="text-[10px] tracking-[0.35em] uppercase text-gold">
            {tk("langswitch.009433", "Idioma")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {tk("langswitch.26cfdd", "Elige tu idioma de lectura")}
          </p>
        </div>
        <ul className="py-2">
          {options.map((o) => {
            const active = o.code === lang;
            return (
              <li key={o.code}>
                <button
                  type="button"
                  onClick={() => {
                    setLang(o.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                    active ? "bg-gold/10" : "hover:bg-gold/5"
                  }`}
                >
                  <span className="text-2xl leading-none">{o.flag}</span>
                  <span className="flex-1">
                    <span className={`block text-sm font-medium ${active ? "text-gold" : "text-foreground"}`}>
                      {o.native}
                    </span>
                    <span className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mt-0.5">
                      {o.region}
                    </span>
                  </span>
                  {active && <Check size={16} className="text-gold" strokeWidth={2} />}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
